package org.teddapp.project.portfolio.export

import com.tedd.db.Competence
import com.tedd.db.Competence_criterion
import com.tedd.db.Competence_family
import com.tedd.db.Portfolio_piece
import com.tedd.db.Portfolio_piece_competence
import org.teddapp.project.db.civilFromDays
import org.teddapp.project.portfolio.CompetenceLevel
import org.teddapp.project.portfolio.PieceStatus
import org.teddapp.project.portfolio.PortfolioCodecs
import org.teddapp.project.portfolio.aggregateLevel
import org.teddapp.project.portfolio.levelDistribution
import kotlin.math.roundToInt

/** Ce qu'on sait d'une skill Focus liée, pour l'export : heures et palier, s'ils existent. */
data class SkillDocInfo(val hours: Double?, val tierLabel: String?)

/** Le seul code d'export qui connaît les tables ; il produit des [DocBlock]. */
object PortfolioDocumentBuilder {

    fun buildRecueil(
        families: List<Competence_family>,
        competences: List<Competence>,
        criteria: List<Competence_criterion>,
        skillInfo: (skillId: String) -> SkillDocInfo?,
        generatedAt: String,
    ): PortfolioDoc {
        val blocks = mutableListOf<DocBlock>()
        val byFamily = competences.groupBy { it.family_id }
        val byCompetence = criteria.groupBy { it.competence_id }
        val orderedFamilies = families.sortedWith(compareBy({ it.order_index }, { it.name }))
        if (orderedFamilies.isEmpty()) blocks += DocBlock.Paragraph("Aucune famille de compétences pour l'instant.")
        orderedFamilies.forEach { family ->
            blocks += DocBlock.Heading(1, family.name)
            family.description?.takeIf { it.isNotBlank() }?.let { blocks += DocBlock.Paragraph(it) }
            val members = byFamily[family.id].orEmpty().sortedWith(compareBy({ it.order_index }, { it.name }))
            if (members.isEmpty()) blocks += DocBlock.Paragraph("Aucune compétence dans cette famille.")
            members.forEach { competence ->
                blocks += DocBlock.Heading(2, competence.name)
                competence.description?.takeIf { it.isNotBlank() }?.let { blocks += DocBlock.Paragraph(it) }
                val info = competence.skill_id?.let(skillInfo)
                if (info != null && (info.hours != null || info.tierLabel != null)) {
                    val pairs = mutableListOf<Pair<String, String>>()
                    info.hours?.let { pairs += "Temps de focus" to fmtHours(it) }
                    info.tierLabel?.let { pairs += "Palier" to it }
                    blocks += DocBlock.KeyValues(pairs)
                }
                val crits = byCompetence[competence.id].orEmpty().sortedBy { it.order_index }
                if (crits.isEmpty()) {
                    blocks += DocBlock.Paragraph("Aucun critère défini.")
                } else {
                    val levels = crits.map { it.level }
                    blocks += DocBlock.LevelBar(levelDistribution(levels), aggregateLevel(levels)?.value)
                    blocks += DocBlock.Bullets(crits.map { c ->
                        buildString {
                            append(CompetenceLevel.of(c.level).label)
                            append(" · ")
                            append(c.label)
                            c.description?.takeIf { it.isNotBlank() }?.let { append(" — ").append(it) }
                            c.evaluated_at?.let { append(" (évalué le ").append(isoFromMillis(it)).append(")") }
                            c.evaluation_note?.takeIf { it.isNotBlank() }?.let { append(" · ").append(it) }
                        }
                    })
                }
            }
        }
        return PortfolioDoc("Recueil de compétences", null, generatedAt, blocks)
    }

    fun buildPortfolio(
        pieces: List<Portfolio_piece>,
        links: List<Portfolio_piece_competence>,
        competences: List<Competence>,
        criteria: List<Competence_criterion>,
        projectNames: Map<String, String>,
        stepTitles: Map<String, String>,
        includeDrafts: Boolean,
        generatedAt: String,
    ): PortfolioDoc {
        val blocks = mutableListOf<DocBlock>()
        val competencesById = competences.associateBy { it.id }
        val criteriaById = criteria.associateBy { it.id }
        val linksByPiece = links.groupBy { it.piece_id }
        val selected = pieces
            .filter { includeDrafts || it.status == PieceStatus.PUBLISHED }
            .sortedWith(compareByDescending<Portfolio_piece> { it.pinned }.thenBy { it.order_index }.thenByDescending { it.updated_at })
        if (selected.isEmpty()) blocks += DocBlock.Paragraph(if (includeDrafts) "Aucune pièce." else "Aucune pièce publiée.")
        selected.forEachIndexed { i, piece ->
            if (i > 0) blocks += DocBlock.Divider
            blocks += DocBlock.Heading(1, piece.title)
            piece.subtitle?.takeIf { it.isNotBlank() }?.let { blocks += DocBlock.Paragraph(it) }
            val meta = mutableListOf<Pair<String, String>>()
            period(piece.date_start, piece.date_end)?.let { meta += "Période" to it }
            piece.project_id?.let { projectNames[it] }?.let { meta += "Projet" to it }
            piece.step_id?.let { stepTitles[it] }?.let { meta += "Étape de parcours" to it }
            if (piece.status != PieceStatus.PUBLISHED) meta += "Statut" to "Brouillon"
            if (meta.isNotEmpty()) blocks += DocBlock.KeyValues(meta)
            if (piece.body.isNotBlank()) blocks += DocBlock.Markdown(piece.body)
            val pieceLinks = PortfolioCodecs.decodeLinks(piece.links)
            if (pieceLinks.isNotEmpty()) blocks += DocBlock.Links(pieceLinks.map { (it.label.ifBlank { it.url }) to it.url })
            val images = PortfolioCodecs.decodeImages(piece.images)
            if (images.isNotEmpty()) blocks += DocBlock.Images(images.map { it.url to it.caption })
            val shown = linksByPiece[piece.id].orEmpty()
                .distinctBy { Triple(it.piece_id, it.competence_id, it.criterion_id) }
                .sortedBy { it.order_index }
                .mapNotNull { link ->
                    val competence = competencesById[link.competence_id] ?: return@mapNotNull null
                    val criterion = link.criterion_id?.let { criteriaById[it] }
                    buildString {
                        append(competence.name)
                        criterion?.let { append(" · ").append(it.label) }
                        link.note?.takeIf { it.isNotBlank() }?.let { append(" (").append(it).append(")") }
                    }
                }
            if (shown.isNotEmpty()) {
                blocks += DocBlock.Paragraph("Compétences démontrées")
                blocks += DocBlock.Tags(shown)
            }
        }
        return PortfolioDoc("Portfolio", null, generatedAt, blocks)
    }

    /** Les deux documents à la suite, chacun sous son propre titre, séparés par un saut de page. */
    fun buildAll(recueil: PortfolioDoc, portfolio: PortfolioDoc, generatedAt: String): PortfolioDoc {
        val blocks = mutableListOf<DocBlock>()
        blocks += DocBlock.Heading(1, portfolio.title)
        blocks += portfolio.blocks.map(::shift)
        blocks += DocBlock.PageBreak
        blocks += DocBlock.Heading(1, recueil.title)
        blocks += recueil.blocks.map(::shift)
        return PortfolioDoc("Portfolio & recueil de compétences", null, generatedAt, blocks)
    }

    private fun shift(block: DocBlock): DocBlock =
        if (block is DocBlock.Heading) block.copy(level = (block.level + 1).coerceAtMost(4)) else block

    internal fun fmtHours(hours: Double): String =
        if (hours >= 10) "${hours.roundToInt()} h" else "${((hours * 10).roundToInt() / 10.0)} h".replace(".0 h", " h")

    internal fun isoFromMillis(millis: Long): String {
        val (y, m, d) = civilFromDays((millis / 86_400_000L).toInt())
        return "${y.toString().padStart(4, '0')}-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}"
    }

    private fun period(start: String?, end: String?): String? = when {
        !start.isNullOrBlank() && !end.isNullOrBlank() -> "$start → $end"
        !start.isNullOrBlank() -> "depuis $start"
        !end.isNullOrBlank() -> "jusqu'au $end"
        else -> null
    }
}
